/**
 * TagTypeahead — FE #257.
 *
 * Notion-style multi-select tag picker for the Browse Tag layout.
 * Type-to-filter the entire tag corpus (no `slice(0, 5)` cap), tap a
 * row to add it to `filters.tags[]`, tap an X on a chip to remove it.
 * Multiple selected tags AND-intersect — Linear's saved-view filter
 * formula semantics.
 *
 * Closes the "wicker" gap explicitly called out by the maintainer:
 * a tag at position #6 in a 200-tag corpus was previously invisible
 * because the only surfaces were the FeaturedTagsStrip (capped) and
 * an alphabetical scroll list with no jump affordance.  This component
 * collapses the entire corpus into 2–3 keystrokes.
 *
 * Design (cited):
 *   - Notion multi-select tag picker — pinned recently-used → A-Z
 *     full list → inline `+ Create` when zero matches, NO mode switch
 *     between pick and create.  Research-bundle gold standard.
 *   - Linear filter formula — AND operator pill renders between chips
 *     so multi-select semantics are explicit, not implied.
 *
 * Wired from `BrowseScreen.tsx` Tag layout.  When
 * `filters.tags.length > 0` the parent screen also collapses the
 * sectioned list to a single AND-intersection section
 * (the section bucket logic stays per-tag for the layout's natural
 * browsable mode; the typeahead drives the filtered mode).
 */

import React, { useMemo, useState } from 'react';
import {
  FlatList,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { useAppDispatch, useAppSelector } from '../../../store/store';
import { fpColors } from '../../../styles/theme';
import {
  selectAllTags,
  selectFilters,
  setFilters,
} from '../browseSlice';

const MAX_DROPDOWN_ROWS = 8;

export const TagTypeahead: React.FC = () => {
  const dispatch = useAppDispatch();
  const filters = useAppSelector(selectFilters);
  const allTags = useAppSelector(selectAllTags);
  const selected = filters.tags ?? [];

  const [query, setQuery] = useState('');
  const [focused, setFocused] = useState(false);

  // Suggestions are computed from the global tag corpus, less anything
  // the user has already chipped (so the dropdown never offers a
  // tag that's already an AND term), filtered by the typed query.
  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    const alreadyPicked = new Set(selected.map((t) => t.toLowerCase()));
    const pool = allTags.filter((t) => !alreadyPicked.has(t.tag.toLowerCase()));
    if (!q) return pool.slice(0, MAX_DROPDOWN_ROWS);
    return pool
      .filter((t) => t.tag.toLowerCase().includes(q))
      .slice(0, MAX_DROPDOWN_ROWS);
  }, [allTags, query, selected]);

  // "No matches" inside an active typed query → offer to create the
  // current query as a future tag.  This v1 doesn't *persist* the new
  // tag (that would need an item-level mutation since tags live on
  // items); instead it stages the literal query into the filter chips
  // so the section list scopes to it.  When the user types more items
  // with this tag the count appears naturally on next render.
  const showCreate =
    query.trim().length > 0 &&
    !suggestions.some(
      (t) => t.tag.toLowerCase() === query.trim().toLowerCase(),
    );

  const addTag = (tag: string): void => {
    const t = tag.trim();
    if (!t) return;
    // Case-insensitive dedupe — if the user already has "Wicker", typing
    // "wicker" should not append a duplicate.
    if (selected.some((s) => s.toLowerCase() === t.toLowerCase())) return;
    dispatch(setFilters({ ...filters, tags: [...selected, t] }));
    setQuery('');
  };

  const removeTag = (tag: string): void => {
    dispatch(
      setFilters({
        ...filters,
        tags: selected.filter((s) => s.toLowerCase() !== tag.toLowerCase()),
      }),
    );
  };

  return (
    <View style={styles.wrap} accessibilityLabel="Tag typeahead">
      {/* ── Selected chips row + AND operator pills ─────────────── */}
      {selected.length > 0 && (
        <View style={styles.chipsRow}>
          {selected.map((tag, i) => (
            <React.Fragment key={`sel-${tag}`}>
              {i > 0 && <Text style={styles.andOperator}>AND</Text>}
              <Pressable
                onPress={() => removeTag(tag)}
                style={({ pressed }) => [
                  styles.selectedChip,
                  pressed && styles.selectedChipPressed,
                ]}
                accessibilityRole="button"
                accessibilityLabel={`Remove tag ${tag}`}
              >
                <Text style={styles.selectedChipLabel}>{tag}</Text>
                <MaterialIcons
                  name="close"
                  size={14}
                  color={fpColors.onPrimary}
                  style={styles.selectedChipX}
                />
              </Pressable>
            </React.Fragment>
          ))}
        </View>
      )}

      {/* ── Typeahead input ───────────────────────────────────── */}
      <View style={styles.inputWrap}>
        <MaterialIcons
          name="search"
          size={18}
          color={fpColors.onSurfaceVariant}
          style={styles.inputIcon}
        />
        <TextInput
          value={query}
          onChangeText={setQuery}
          onFocus={() => setFocused(true)}
          onBlur={() => {
            // Delay the blur so taps on dropdown rows register before
            // the dropdown disappears.  Skipping this collapses the
            // dropdown out from under the user's finger on web.
            setTimeout(() => setFocused(false), 120);
          }}
          placeholder="Filter by tag — e.g. wicker, garage, saddleridge"
          placeholderTextColor={fpColors.onSurfaceVariant}
          style={styles.input}
          autoCorrect={false}
          autoCapitalize="none"
          // Submitting (return) selects the first suggestion — quick
          // typing-only flow without hand-off to mouse/touch.
          onSubmitEditing={() => {
            if (suggestions.length > 0) addTag(suggestions[0].tag);
            else if (showCreate) addTag(query.trim());
          }}
          accessibilityLabel="Tag filter input"
        />
        {query.length > 0 && (
          <Pressable
            onPress={() => setQuery('')}
            accessibilityRole="button"
            accessibilityLabel="Clear tag search"
            hitSlop={8}
          >
            <MaterialIcons
              name="close"
              size={18}
              color={fpColors.onSurfaceVariant}
            />
          </Pressable>
        )}
      </View>

      {/* ── Dropdown — only when focused AND we have rows to show ── */}
      {focused && (suggestions.length > 0 || showCreate) && (
        <View style={styles.dropdown}>
          <FlatList
            data={suggestions}
            keyboardShouldPersistTaps="handled"
            keyExtractor={(item) => `sug-${item.tag}`}
            renderItem={({ item }) => (
              <Pressable
                onPress={() => addTag(item.tag)}
                style={({ pressed }) => [
                  styles.row,
                  pressed && styles.rowPressed,
                ]}
                accessibilityRole="button"
                accessibilityLabel={`Add tag ${item.tag}`}
              >
                <Text style={styles.rowLabel} numberOfLines={1}>
                  {item.tag}
                </Text>
                <Text style={styles.rowCount}>
                  {item.count === 1 ? '1 item' : `${item.count} items`}
                </Text>
              </Pressable>
            )}
            ListFooterComponent={
              showCreate ? (
                <Pressable
                  onPress={() => addTag(query.trim())}
                  style={({ pressed }) => [
                    styles.row,
                    styles.createRow,
                    pressed && styles.rowPressed,
                  ]}
                  accessibilityRole="button"
                  accessibilityLabel={`Create tag ${query.trim()}`}
                >
                  <MaterialIcons
                    name="add"
                    size={16}
                    color={fpColors.primary}
                    style={{ marginRight: 6 }}
                  />
                  <Text style={styles.createLabel} numberOfLines={1}>
                    {`Create tag “${query.trim()}”`}
                  </Text>
                </Pressable>
              ) : null
            }
          />
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  wrap: {
    paddingHorizontal: 16,
    paddingBottom: 4,
  },
  chipsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    marginBottom: 6,
    gap: 6,
  },
  selectedChip: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
    backgroundColor: fpColors.primary,
  },
  selectedChipPressed: {
    opacity: 0.7,
  },
  selectedChipLabel: {
    color: fpColors.onPrimary,
    fontSize: 12,
    fontWeight: '600',
    textTransform: 'capitalize',
  },
  selectedChipX: {
    marginLeft: 4,
  },
  andOperator: {
    fontSize: 10,
    fontWeight: '700',
    color: fpColors.onSurfaceVariant,
    letterSpacing: 0.6,
    paddingHorizontal: 2,
  },
  inputWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: fpColors.surfaceContainerLowest,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    paddingHorizontal: 10,
    paddingVertical: Platform.OS === 'ios' ? 8 : 4,
  },
  inputIcon: {
    marginRight: 6,
  },
  input: {
    flex: 1,
    fontSize: 14,
    color: fpColors.onSurface,
    padding: 0,
    margin: 0,
  },
  dropdown: {
    marginTop: 6,
    backgroundColor: fpColors.surfaceContainerLowest,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    overflow: 'hidden',
    maxHeight: 280,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: fpColors.outlineVariant,
  },
  rowPressed: {
    backgroundColor: fpColors.surfaceContainerLow,
  },
  rowLabel: {
    flex: 1,
    fontSize: 14,
    fontWeight: '500',
    color: fpColors.onSurface,
    textTransform: 'capitalize',
  },
  rowCount: {
    fontSize: 11,
    color: fpColors.onSurfaceVariant,
  },
  createRow: {
    borderBottomWidth: 0,
  },
  createLabel: {
    flex: 1,
    fontSize: 13,
    fontWeight: '600',
    color: fpColors.primary,
  },
});

export default TagTypeahead;
